import React, { useEffect, useState } from 'react'
import classes from './styles.module.scss'
import { useTranslation } from 'react-i18next';
import { getAllPsdData } from '../../../../requests/public-psd.request';
import { useKato } from '../../../../contexts/kato.context';
import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from 'recharts';
import Card from '../../../../components/card/card.component';
import { monthList } from '../../../../consts/other.conts';

const years = ['all-years', '2022', '2023', '2024', '2025'];
const translationKey = 'cards.psd-card';

const sourceColors = {
  GOVERNMENTAL_INVESTMENT: '#118F84',
  NON_GOVERNMENTAL_INVESTMENT: '#5BC7D9',
  QUASI_GOVERNMENTAL_INVESTMENT: '#92D050',
};

interface FinancingDataPoint {
  monthLabel: string;
  GOVERNMENTAL_INVESTMENT: number;
  NON_GOVERNMENTAL_INVESTMENT: number;
  QUASI_GOVERNMENTAL_INVESTMENT: number;
}

interface PsdData {
  process: { process: string; count: number }[];
  category: { category: string; count: number }[];
  financing: FinancingDataPoint[];
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background: '#fff', padding: '4px 8px', border: '1px solid #ccc' }}>
        {payload.map((entry: any, index: number) => (
          <div key={index} style={{ color: entry.color, fontSize: '0.75rem', fontWeight: 400 }}>
            {entry.value}
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export const CustomLegend = ({ payload, translationKey }: any) => {
  const { t } = useTranslation();

  return (
    <ul style={{
      display: 'flex',
      gap: '0.625rem',
      padding: 0,
      listStyle: 'none',
      fontSize: '0.625rem',
      fontWeight: 400,
      justifyContent: 'center',
      color: '#818689',
      flexWrap: 'wrap',
    }}>
      {payload.map((entry: any) => (entry.dataKey !== 'total' &&
        <li key={entry.dataKey} style={{ display: 'flex', alignItems: 'center' }}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: entry.color,
              marginRight: 6,
            }}
          />
          {t(`${translationKey}.${entry.dataKey}`)}
        </li>
      ))}
    </ul>
  );
};


const AllPsdTab = () => {
  const { t } = useTranslation();
  const { getCurrentKato } = useKato();
  const [selectedYears, setSelectedYears] = useState<string[]>(['2024']);
  const [data, setData] = useState<PsdData>({
    process: [],
    category: [],
    financing: [],
  });

  const handleCheckboxChange = (year: string) => {
    if (year === 'all-years') {
      if (selectedYears.length === years.length - 1) {
        setSelectedYears([]);
      } else {
        setSelectedYears(years.filter(y => y !== 'all-years'));
      }
    } else {
      let updatedSelectedYears = [...selectedYears];

      if (selectedYears.includes(year)) {
        updatedSelectedYears = updatedSelectedYears.filter(y => y !== year);
      } else {
        updatedSelectedYears.push(year);
      }

      const yearGroup = ['2022', '2023', '2024'];
      const allGroupSelected = yearGroup.every(y => updatedSelectedYears.includes(y));

      if (allGroupSelected) {
        updatedSelectedYears = [...new Set([...updatedSelectedYears, ...yearGroup])];
      }

      setSelectedYears(updatedSelectedYears);
    }
  };

  useEffect(() => {
    if (!getCurrentKato()) return;
    const katoValue = getCurrentKato()?.kato;
    if (!katoValue || selectedYears.length === 0) return;
    getAllPsdData(+katoValue, selectedYears).then((data) => {
      const groupedData = Array.from({ length: 12 }, (_, i) => {
        const month = i + 1; // months are 1-indexed in the data
        const entry = data.financing.filter((e: any) => e.month === month);
        return {
          monthLabel: t(`months.${monthList[month - 1]}-short`),
          GOVERNMENTAL_INVESTMENT: entry.find((e: any) => e.financingSource === 'GOVERNMENTAL_INVESTMENT')?.count || 0,
          NON_GOVERNMENTAL_INVESTMENT: entry.find((e: any) => e.financingSource === 'NON_GOVERNMENTAL_INVESTMENT')?.count || 0,
          QUASI_GOVERNMENTAL_INVESTMENT: entry.find((e: any) => e.financingSource === 'QUASI_GOVERNMENTAL_INVESTMENT')?.count || 0,
        };
      });

      setData({
        process: data.process.sort((a: any, b: any) => b.count - a.count) || [],
        category: data.category.sort((a: any, b: any) => b.count - a.count) || [],
        financing: groupedData,
      })
    });
  }, [selectedYears]);

  return (
    <div className={classes['all-psd']}>
      <div className={classes['all-psd__years']}>
        {years.map((year) => (
          <label className='container'>
            <input
              type="checkbox"
              checked={year === 'all-years' ? selectedYears.length === years.length - 1 : selectedYears.includes(year)}
              onChange={() => handleCheckboxChange(year)}
              id={year}
              name={year}
            />
            <div
              className={classes['all-psd__year']}>
              {t(`${translationKey}.${year}`)}
            </div>
          </label>
        ))}
      </div>

      <div className={classes['all-psd__charts']}>
        <Card title={t(`${translationKey}.process.title`)} subtitle={t(`${translationKey}.process.subtitle`)}>
          <ResponsiveContainer width="100%" height={'100%'}>
            <BarChart
              layout="vertical"
              data={data.process.slice(0, 5) || []}
              margin={{ top: 10, right: 20, left: 20, bottom: 0 }}
              barSize={10}
            >
              <CartesianGrid vertical={false} horizontal={true} stroke='#DBDCE0' />
              <XAxis type="number" tick={{ fontSize: '0.625rem', fontWeight: 400, fill: '#7C7E7C' }} tickLine={false}
                axisLine={false} />
              <YAxis
                type="category"
                dataKey={'process'}
                tick={({ x, y, payload }) => (
                  <text
                    x={x - 100}
                    y={y + 4}
                    fontSize={'0.75rem'}
                    fontWeight={400}
                    fill="#202224"
                    textAnchor="start"
                  >
                    {payload.value}
                  </text>
                )}
                width={100}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" fill="var(--color-primary)" radius={[0, 1, 2, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title={t(`${translationKey}.category.title`)} subtitle={t(`${translationKey}.category.subtitle`)}>
          <ResponsiveContainer width="100%" height={'100%'}>
            <BarChart
              layout="vertical"
              data={data.category.slice(0, 5) || []}
              margin={{ top: 10, right: 20, left: 20, bottom: 0 }}
              barSize={10}
            >
              <CartesianGrid vertical={false} horizontal={true} stroke='#DBDCE0' />
              <XAxis type="number" tickLine={false}
                axisLine={false} tick={{ fontSize: '0.625rem', fontWeight: 400, fill: '#7C7E7C' }} />
              <YAxis
                type="category"
                dataKey={'category'}
                tick={({ x, y, payload }) => (
                  <text
                    x={x - 100}
                    y={y + 4}
                    fontSize={'0.75rem'}
                    fontWeight={400}
                    fill="#202224"
                    textAnchor="start" // выравнивание влево
                  >
                    {payload.value}
                  </text>
                )}
                width={100}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" fill="var(--color-primary)" radius={[0, 2, 2, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title={t(`${translationKey}.financing.title`)}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data.financing}
              margin={{ top: 20, right: 20, left: 20, bottom: 10 }}
            >
              <CartesianGrid vertical={false} horizontal={true} stroke='#DBDCE0' />
              <XAxis
                dataKey="monthLabel"
                tick={{ fontSize: '0.75rem', fontWeight: 400, fill: '#818689' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: '0.75rem', fontWeight: 400, fill: '#818689' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip />
              <Legend content={<CustomLegend translationKey={translationKey} />} />
              {Object.entries(sourceColors).map(([key, color]) => (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId="a"
                  fill={color}
                  radius={key === 'QUASI_GOVERNMENTAL_INVESTMENT' ? [10, 10, 0, 0] : 0} // верхние скругления на последнем bar
                  barSize={30}
                  name={t(`${translationKey}.${key}`).split(' ')[0]}
                >
                  <LabelList
                    dataKey={key}
                    position="center"
                    fill="#fff"
                    fontSize={'0.625rem'}
                  />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Card>

      </div>
    </div>
  )
}

export default AllPsdTab